import { FC, ReactNode, memo, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';

import { PunchupEntityTile } from 'features/common/components/Punchup/primitives/display/PunchupTile';
import { PunchupTicketTypeTag } from 'features/common/components/Punchup/primitives/display/PunchupTicketTypeTag';
import styles from './PunchupPoolTile.module.css';

export interface PunchupPoolTileProps {
	/** Pool name — the tile's primary text (16px / 600). */
	name: string;
	/**
	 * The shared-capacity headline shown as the tile's small top-left label, e.g. "12 / 50 used" on the
	 * Show Dashboard or "38 / 50 available" on Send Tickets — reciprocal framing (used + available = total)
	 * over the SAME Lifetime-Σ figures, so the two screens reconcile.
	 */
	headline: string;
	/** Member ticket-type names — rendered as a label-less chip row. */
	memberNames: string[];
	/**
	 * Member ticket-type colours, parallel to `memberNames` — the chip for member `i` is tinted with
	 * `memberColors[i]`. Resolve from `useTicketTypeColors` at the (data-aware) callsite; omit to fall
	 * back to neutral grey chips. A composite stays data-free, so the colours are passed in, not fetched.
	 */
	memberColors?: (string | null)[];
	/** Over-subscribed (Σ member issued > total): renders a warning icon top-right. */
	overCapacity?: boolean;
	/** Optional muted note line(s), e.g. "No ticket types assigned" for a pool with no members. */
	descriptions?: string[];
	/** Bottom-right help tooltip — pass a span-based `PunchupHelpButton` (safe inside a clickable tile). */
	helpButton?: ReactNode;
	/** When set the whole tile is clickable (Show Dashboard → opens the inline edit form). */
	onClick?: () => void;
	/** Live over-reach: a red ring (Send Tickets bulk mode, when requested across members > remaining). */
	exceeded?: boolean;
	dataCy?: string;
}

/**
 * The single pool tile, shared by the Show-Dashboard Pools accordion and the Send Tickets pool row, mirroring
 * how `PunchupTicketTypeTile` is shared. Both callers feed the SAME Lifetime-Σ numbers from `useEventCapacity`
 * (the dashboard frames them as "used", Send as "available"), so the screens reconcile by construction. Wraps
 * `PunchupEntityTile`: the headline X/Y is the tile title, the pool name is the primary text, members are a
 * label-less chip row, an over-capacity warning icon sits top-right, and an optional help tooltip bottom-right.
 */
export const PunchupPoolTile: FC<PunchupPoolTileProps> = memo(
	({
		name,
		headline,
		memberNames,
		memberColors,
		overCapacity,
		descriptions,
		helpButton,
		onClick,
		exceeded,
		dataCy,
	}) => {
		// Colour-tinted member chips — the SAME PunchupTicketTypeTag the tables use. Built here (a composite
		// may compose primitives) and handed to the tile via tagSlot, so the PunchupEntityTile primitive
		// stays chip-agnostic. Memoised so the memoised tile isn't re-rendered by a fresh node each pass.
		const memberChips = useMemo(
			() =>
				memberNames.map((member, i) => <PunchupTicketTypeTag key={member} name={member} color={memberColors?.[i]} />),
			[memberNames, memberColors]
		);

		return (
			<div className={exceeded ? styles.exceededWrapper : undefined} data-exceeded={exceeded || undefined}>
				<PunchupEntityTile
					title={headline}
					name={name}
					descriptions={descriptions}
					singleLineDescriptions
					tagSlot={memberNames.length > 0 ? memberChips : undefined}
					topRight={
						overCapacity ? (
							<AlertTriangle size={16} className={styles.warningIcon} aria-label="Over capacity" />
						) : undefined
					}
					helpButton={helpButton}
					onClick={onClick}
					dataCy={dataCy}
				/>
			</div>
		);
	}
);

PunchupPoolTile.displayName = 'PunchupPoolTile';
